/** Raw API values whose readable label cannot be derived by the generic rule. */
const knownLabels: Record<string, string> = {
  DYNAMIC: 'Dynamic',
  FIXED: 'Fixed',
  FREE: 'Free',
  POWER: 'Power based',
  TOU: 'Time of use',
  CHADEMO: 'CHAdeMO',
};

const acronyms = new Set(['AC', 'DC', 'CCS', 'NACS', 'SAE', 'EV', 'EVSE', 'DCFC', 'GBT', 'IEC', 'OCPP', 'USB']);

// Acronyms and model codes stay in capitals: CCS, NACS, J1772, V3…
const isCode = (word: string) => /\d/.test(word) || acronyms.has(word.toUpperCase());

const titleCaseWord = (word: string) => (isCode(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const humanizeWords = (value: string, caseWord: (word: string) => string) => {
  const trimmed = value.trim();
  const known = knownLabels[trimmed.toUpperCase()];
  if (known) return known;
  return trimmed
    .split(/[\s_]+/)
    .filter(Boolean)
    .map(caseWord)
    .join(' ');
};

/**
 * Turns a raw API value into a readable label: DYNAMIC → Dynamic, TOU → Time of use, V3:Adapter → V3 (adapter).
 * Missing values are shown as an em dash.
 */
export const humanizeApiValue = (value: string | null | undefined): string => {
  if (!value?.trim()) return '—';

  const [main, ...details] = value.split(':');
  const detail = details.join(':').trim();
  const label = humanizeWords(main, titleCaseWord);

  return detail ? `${label} (${humanizeWords(detail, (word) => (isCode(word) ? word.toUpperCase() : word.toLowerCase()))})` : label;
};

/** Readable, de-duplicated labels of a list of raw API values; an em dash when there is none. */
export const humanizeApiValues = (values: readonly (string | null)[] | null | undefined): string => {
  const labels = Array.from(new Set((values ?? []).filter((v): v is string => !!v?.trim()).map(humanizeApiValue)));
  return labels.length ? labels.join(', ') : '—';
};
